import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import Measure from "react-measure";
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';
import { CSSTransition } from "react-transition-group";
import { FileIcon } from "@netapp/bxp-style/react-icons/General";
import { fadeClassNames } from "../../animations";
import ActionMenu from "./../ActionMenu/ActionMenu";
import { Button as ActionMenuButton } from "./../ActionMenu/ActionMenuItems";
import BlankRow from "./BlankRow";
import ColumnsSelector from "./ColumnsSelector";
import DefaultRowRenderer from "./DefaultRowRenderer";
import RenderHeaderCell from "./RenderHeaderCell";
import scrollFixStyles from "./ScrollFix.module.scss";
import { default as styles, default as tableStyles } from "./Table.module.scss";
import ToggleColumnSelectorButton from "./ToggleColumnSelectorButton";
import selectingColumn from "./selectingColumn";
import useColumnResize from "./useColumnResize";
import { SELECTING_COL_WIDTH_DELTA, TABLE_COL_MIN_WIDTH, TABLE_ROW_MENU_ID, TABLE_ROW_MENU_WIDTH_2, TOGGLE_COLUMN_SELECTOR_WIDTH } from "./TableConstants";

const emptyFilterState = { columns: {}, count: 0 };
const emptyFilterOptions = {};
const emptyRows = [];

/**
 * rowMenu can be function to generate menu array or {menu, isDisabled}
 */
const menuColumn = (rowMenu) => {
    const rowMenuFunc = _.isFunction(rowMenu) ? rowMenu : rowMenu.menu;
    const isDisabledFunc = rowMenu.isDisabled;

    return {
        id: TABLE_ROW_MENU_ID,
        width: TABLE_ROW_MENU_WIDTH_2,
        fixedWidth: true,
        header: "",
        accessor: "",
        cellClass: tableStyles['menu-cell'],
        sort: {
            enabled: false
        },
        filter: {
            enabled: false
        },
        Renderer: ({ row }) => {
            const isDisabled = (isDisabledFunc && isDisabledFunc(row)) || row.isDisabled || row.isRowMenuDisabled;

            const menu = (row) => {
                return rowMenuFunc(row).map(({ label, onClick = _.noop, Component, ActionMenuItemComponent, disabled = false }) => {
                    if (ActionMenuItemComponent) {
                        return <ActionMenuItemComponent key={label} />;
                    } else if (label || Component) {
                        return <ActionMenuButton isDisabled={disabled} key={label} onClick={onClick}>{Component ? <Component /> : label}</ActionMenuButton>
                    }
                    return null;
                });
            }

            return <>
                <ActionMenu
                    isDisabled={isDisabled}
                    className={tableStyles['table-menu']}
                    isOpened={row.isMenuOpened}
                    onOpenOrClose={row.onMenuOpenOrClose}
                    disabledReason={isDisabled && row.disabledReason ? row.disabledReason : undefined}
                    Trigger="oval-hidden" children={() => menu(row)}
                />
            </>
        }
    }
}

function defaultRowRendererFn () {
    return <DefaultRowRenderer />
}

/**
 * "lastColumnSticky" prop is deprecated. Last column will be sticky by default if it has any actions (columnSelector or RowMenu).
 */
const Table = React.memo(({
                              rows: _rows,
                              columns: _columns,
                              table,
                              fixedHeight,
                              sortState: _sortState,
                              toggleSort: _toggleSort,
                              filterOptions: _filterOptions,
                              filterState: _filterState,
                              updateFilterState: _updateFilterState,
                              selectionState: _selectionState,
                              toggleRowSelection: _toggleRowSelection,
                              rowState: _rowState,
                              updateRowState: _updateRowState,
                              selectAllRows: _selectAllRows,
                              className,
                              cellClassName,
                              rowClassName,
                              rowsContainerClassName,
                              headerContainerClassName,
                              headerCellClassName,
                              headerHandleClassName,
                              headerRowClassName,
                              headerContainerStyle,
                              isSticky,
                              autoSticky,
                              lastColumnSticky,
                              stickColumnIndex = 0,
                              isLoading,
                              RenderRow = defaultRowRendererFn(),
                              rowMenu,
                              onRowClick,
                              onRowDoubleClick,
                              columnState: _columnState,
                              updateColumnState: _updateColumnState,
                              isTogglingColumns,
                              restoreColumnStateDefaults: _restoreColumnStateDefaults,
                              originalColumns,
                              toggleColumnSelectorClassName,
                              toggleColumnSelectorStyle,
                              columnSelectorClassName,
                              SubRowRenderer,
                              noDataLabel = "No Data",
                              isRowDisabled,
                              disabledTitle,
                              innerTable= false 
                          }) => {
    const headersContainerRef = useRef(null);
    const tableRef = useRef(null);
    const rowsContainerRef = useRef(null);
    const scrollDuplicatorRef = useRef(null);
    const lastColumnStickyStyleRef = useRef({ dataset: { styleid: `Table-last-sticky-${Math.random().toString(36).substr(2)}` } });
    const customColumnStickyStyleRef = useRef({ dataset: { styleid: `Table-custom-sticky-${Math.random().toString(36).substr(2)}` } });
    const [addColumnsOverlayActive, setAddColumnsOverlayState] = useState(false);
    const columnSelectorRef = useRef(null);

    const rows = _rows || table?.organizedRows || emptyRows;
    const $columns = _columns || table?.columns || [];
    const sortState = _sortState || table?.sortState;
    const toggleSort = _toggleSort || table?.toggleSort;
    const filterOptions = _filterOptions || table?.filterOptions || emptyFilterOptions;
    const filterState = _filterState || table?.filterState || emptyFilterState;
    const updateFilterState = _updateFilterState || table?.updateFilterState || _.noop;
    const selectionState = _selectionState || table?.selectionState;
    const toggleRowSelection = _toggleRowSelection || table?.toggleRowSelection;
    const rowState = _rowState || table?.rowState;
    const updateRowState = _updateRowState || table?.updateRowState;
    const selectAllRows = _selectAllRows || table?.selectAllRows;
    const columnState = _columnState || table?.columnState;
    const updateColumnState = _updateColumnState || table?.updateColumnState;
    const restoreColumnStateDefaults = _restoreColumnStateDefaults || table?.restoreColumnStateDefaults;

    const isRowSelectingEnabled = _.get(selectionState, "rows", false);
    const isLastColumnSticky = (isTogglingColumns || rowMenu || SubRowRenderer) ? true : lastColumnSticky;
    const renderMode = { type: "default" };

    if (!_.isUndefined(_.get(RenderRow, 'props.childKey'))) renderMode.type = "tree";

    const columns = React.useMemo(() => {
        const pColumns = $columns.map(column => ({ ...column, width: column.width || 1 }));

        if (rowMenu) {
            pColumns.push(menuColumn(rowMenu))
        }

        if(isRowSelectingEnabled) {
            pColumns.unshift(selectingColumn(innerTable, renderMode.type, selectAllRows ? SELECTING_COL_WIDTH_DELTA : 0));
        }
        
        // Add Empty Column to avoid column header overlap with column picker icon.
        // If there is a row menu or subRowRenderer, don't add the Empty Column since the row menu or subRowRenderer already has a column.
        if(isTogglingColumns && !rowMenu && !SubRowRenderer) {
            pColumns.push({ header: '', accessor: 'empty_id', id: 'empty_id', width: TOGGLE_COLUMN_SELECTOR_WIDTH, fixedWidth: true, sort: { enabled: false}});
        }

        return pColumns;
    }, [$columns, rowMenu, isRowSelectingEnabled]);

    const [scrollDividerTop, setScrollDividerTop] = useState(0);
    const [hasScroll, setHasScroll] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [totalWidth, setTotalWidth] = useState(0);
    const [tableWidth, setTableWidth] = useState(0);
    const [scrollDividerLeft, setScrollDividerLeft] = useState(0);
    const [stickyTop, setStickyTop] = useState(undefined);
    let defaultWidths = _.range(columns.length).map(() => 0);
    const { widths, setWidths, handleColumnResize } = useColumnResize(columns, defaultWidths);

    const scrollRows = (e) => {
        const newScrollLeft = e.target.scrollLeft;
        if (headersContainerRef.current) {
            headersContainerRef.current.scrollLeft = newScrollLeft;
            if (rowsContainerRef.current) {
                rowsContainerRef.current.scrollLeft = newScrollLeft;
            }

            if (newScrollLeft > 0) {
                setIsScrolled(true);
                customColumnStickyStyleRef.current.innerHTML = `.${customColumnStickyStyleRef.current.dataset.styleid} {
                    z-index: 1;
                    transform: translateX(${newScrollLeft}px);
                }`;

                lastColumnStickyStyleRef.current.innerHTML = `.${lastColumnStickyStyleRef.current.dataset.styleid} {
                    z-index: 1;
                    transform: translateX(-${totalWidth - tableWidth - newScrollLeft}px);
                }`;
            } else if (newScrollLeft === 0) {
                setIsScrolled(false);
                customColumnStickyStyleRef.current.innerHTML = ``;
                lastColumnStickyStyleRef.current.innerHTML = `.${lastColumnStickyStyleRef.current.dataset.styleid} {
                    z-index: 1;
                    transform: translateX(-${totalWidth - tableWidth}px);
                }`;
            }
        }
    };

    useLayoutEffect(() => {
        const handleResize = () => {
            const width = tableRef.current.getBoundingClientRect().width;

            const totalWidth = _.sumBy(columns, "width");
            setWidths(_.map(columns, "width"));
            if (totalWidth > width) {
                setTotalWidth(totalWidth);
                setHasScroll(true);
            } else {
                setTotalWidth(width);
            }
            setTableWidth(width);

            setTimeout(() => {

                const childNodes = headersContainerRef?.current?.childNodes[0]?.childNodes
                // calculating the devider by summing all column widths up to column which equal to the sticky index
                const leftDivider = _.sumBy(_.take(childNodes, stickColumnIndex + 1), node => node?.getBoundingClientRect()?.width || 0);
                setScrollDividerLeft(leftDivider);

                lastColumnStickyStyleRef.current.innerHTML = `.${lastColumnStickyStyleRef.current.dataset.styleid} {
                    z-index: 1;
                    transform: translateX(-${totalWidth - width - (headersContainerRef?.current?.scrollLeft || 0)}px);
                }`;
            });
        };

        handleResize();

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [tableRef, setWidths, setTotalWidth, columns]);

    useLayoutEffect(() => {
        setScrollDividerTop(
            (headersContainerRef?.current?.childNodes[0]?.childNodes[0]?.getBoundingClientRect()?.height || 0)
        );
    }, []);


    const hasExpandedRow = useMemo(() => _.some(rowState, 'isExpanded'), [rowState])

    useEffect(() => {

        const lastColumnStyleId = lastColumnStickyStyleRef.current.dataset.styleid;
        lastColumnStickyStyleRef.current = window.document.createElement('style');
        lastColumnStickyStyleRef.current.type = 'text/css';
        lastColumnStickyStyleRef.current.dataset.styleid = lastColumnStyleId;

        const customColumnStyleId = customColumnStickyStyleRef.current.dataset.styleid;
        customColumnStickyStyleRef.current = window.document.createElement('style');
        customColumnStickyStyleRef.current.type = 'text/css';
        customColumnStickyStyleRef.current.dataset.styleid = customColumnStyleId;

        window.document.head.appendChild(lastColumnStickyStyleRef.current);
        window.document.head.appendChild(customColumnStickyStyleRef.current);

        return () => {
            window.document.head.removeChild(lastColumnStickyStyleRef.current);
            window.document.head.removeChild(customColumnStickyStyleRef.current);
        };
    }, []);

    function rowRendererFn (rows) {

        const rowClickHandler = (row, e) => {
            if(onRowClick && !e.actionMenuClicked) {
                onRowClick(row);
            }
        }

        // If the user provides a custom rowRenderer, merge its props with the following default props.
        const rowRenderer = React.cloneElement(RenderRow, {
            columns: columns,
            rows: rows,
            rowClassName: rowClassName,
            cellClassName: cellClassName,
            widths: widths,
            totalWidth: totalWidth,
            isRowSelectingEnabled: isRowSelectingEnabled,
            isMultiSelect: isRowSelectingEnabled && selectionState.isMulti,
            selectionState: selectionState,
            toggleRowSelection: toggleRowSelection,
            rowState: rowState,
            updateRowState: updateRowState,
            onRowClick: rowClickHandler,
            onRowDoubleClick: onRowDoubleClick,
            lastColumnSticky: isLastColumnSticky,
            lastColumnStickyStyleRef: lastColumnStickyStyleRef,
            stickColumnIndex: stickColumnIndex,
            stickyColumnStyleRef: customColumnStickyStyleRef,
            SubRowRenderer: SubRowRenderer,
            isRowDisabled: isRowDisabled,
            disabledTitle: disabledTitle,
            innerTable: innerTable,
            tableWidth: tableWidth,
            isLoading: isLoading
        });

        return <React.Fragment>
            {rowRenderer}
        </React.Fragment>;
    }

    const getColMinWidth = (colIndex) => {
        const minWidth = columns[colIndex].minWidth || TABLE_COL_MIN_WIDTH;
        return Math.max(minWidth, TABLE_COL_MIN_WIDTH);
    }

    return <>
        <div className={classNames(styles['base'], className,  {[styles['inner-table']]: innerTable})} ref={tableRef}>
            {isSticky && autoSticky && <Measure
                bounds
                onResize={contentRect => {
                    setStickyTop(contentRect.bounds.height + contentRect.bounds.top - (_.isNumber(autoSticky) ? autoSticky : 0));
                }}
            >
                {({ measureRef }) => <div ref={measureRef}/>}
            </Measure>}
            <div className={classNames(styles['headers-group-container'], headerContainerClassName, {[styles['sticky-header']]: isSticky})}
                 style={{ top: stickyTop, ...headerContainerStyle }} ref={headersContainerRef}>
                <BlankRow stickColumnIndex={stickColumnIndex} stickyColumnStyleRef={customColumnStickyStyleRef}
                          columns={columns} widths={widths}/>
                <div className={classNames(styles['header-row'], headerRowClassName)} data-testid={'table-header-row'}>
                    {columns.map((column, index) => {
                        const stickLastColumn = isLastColumnSticky && index === columns.length - 1;
                        const stickColumn = index <= stickColumnIndex;
                        const HeaderRenderer = column.HeaderRenderer || RenderHeaderCell;
                        const colMinWidth = Math.max(_.get(column, "minWidth", 0), TABLE_COL_MIN_WIDTH);
                        const colWidth = widths ? widths[index] : (column.width || colMinWidth);

                        return <HeaderRenderer headerCellClassName={headerCellClassName}
                                               headerHandleClassName={headerHandleClassName}
                                                 stickLastColumn={stickLastColumn}
                                                 lastColumnStickyStyleRef={lastColumnStickyStyleRef}
                                                 stickyColumnStyleRef={customColumnStickyStyleRef}
                                                 stickColumn={stickColumn}
                                                 selectionState={selectionState}
                                                 selectAllRows={selectAllRows}
                                                 columnFilterOptions={filterOptions[column.id]}
                                                 columnFilterState={filterState.columns[column.id]}
                                                 updateFilterState={updateFilterState}
                                                 columnIndex={index}
                                                 key={column.id}
                                                 width={colWidth}
                                                 column={column}
                                                 sortState={sortState}
                                                 toggleSort={toggleSort}
                                                 onResize={(e) => handleColumnResize(index, e)}
                                                 minWidth={getColMinWidth(index)}
                                                 innerTable={innerTable}
                                                 dataTestId={`table-column-header-${column.header === "" ? column.id : column.header}`}/>
                    })}
                </div>
                {isTogglingColumns &&
                <ToggleColumnSelectorButton className={toggleColumnSelectorClassName} style={toggleColumnSelectorStyle}
                                            columnStickyStyleRef={customColumnStickyStyleRef}
                                            isActive={addColumnsOverlayActive}
                                            toggle={() => setAddColumnsOverlayState(!addColumnsOverlayActive)}/>}
            </div>
            {isTogglingColumns &&
            <CSSTransition nodeRef={columnSelectorRef} in={addColumnsOverlayActive} unmountOnExit timeout={400}
                           classNames={fadeClassNames}>
                <ColumnsSelector className={columnSelectorClassName} ref={columnSelectorRef} isSticky={isSticky}
                                 originalColumns={originalColumns} columnState={columnState}
                                 updateColumnState={updateColumnState}
                                 restoreColumnStateDefaults={restoreColumnStateDefaults}
                                 closeSelector={() => setAddColumnsOverlayState(false)}/>
            </CSSTransition>
            }
            {!isLoading && rows.length === 0 && <div className={styles['no-table-data']}>
                <FileIcon/>
                {noDataLabel}
            </div>}

            {hasScroll && isScrolled && stickColumnIndex >= 0 && !hasExpandedRow && <div className={scrollFixStyles['scroll-divider']} style={{
                top: scrollDividerTop,
                left: scrollDividerLeft
            }}/>}
            {addColumnsOverlayActive && <div className={styles['rows-group-cover']}/>}
            {(isLoading || rows.length > 0) && <div
                className={classNames(styles['rows-group-container'], rowsContainerClassName, { [styles['fixed-height']]: !!fixedHeight })}>
                <div ref={rowsContainerRef} style={fixedHeight ? { maxHeight: fixedHeight } : undefined}>
                    {rowRendererFn(rows)}
                </div>
            </div>}
            <InView>
                {({ inView, ref, entry }) => {
                    return <>
                        <div ref={ref}/>
                        {!isLoading && hasScroll && <div
                            className={classNames(scrollFixStyles["scroll-duplicator"], { [scrollFixStyles["scroll-duplicator-sticky"]]: isSticky && !inView })}
                            style={{ width: tableWidth }} onScroll={(e) => scrollRows(e)} ref={scrollDuplicatorRef}>
                            <div style={{ width: totalWidth }}/>
                        </div>}
                    </>
                }}
            </InView>
        </div>
    </>
});

Table.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        header: PropTypes.string,
        headerClass: PropTypes.string,
        id: PropTypes.string,
        accessor: PropTypes.string,
        sort: PropTypes.shape({
            enabled: PropTypes.bool //default: true
        }),
        popoverText: PropTypes.string
    }).isRequired).isRequired,
    className: PropTypes.string,
    sortState: PropTypes.object,
    toggleSort: PropTypes.func,
    filterOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
    }))),
    filterState: PropTypes.object,
    updateFilterState: PropTypes.func,
    isLoading: PropTypes.bool,
};

Table.displayName = "Table";

export default Table;
